import { IScope, IQService, IPromise, IRootScopeService } from "angular";
import { StateParams, StateService } from "@uirouter/angularjs";
import { IUser } from "../models";
import { UserService } from "../userService";
import { AccountService } from "../../accounts/accounts-service";
import { ICollection } from "../../models";
import { IAccountSubscription } from "../../configuration/models";
import { UserProfileService } from "../userProfile/profileService";
import { IProfile } from "@webapp/users/models/users.models";
import { ICustomField } from "../../customFields/models/models";
import { IAssigneeIdMap } from "../../assignees/models";
import { IStateInit } from "@gtmhub/core/routing";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { CustomFieldActions, ICustomFieldsStoreState } from "@gtmhub/customFields";
import { INgRedux } from "@gtmhub/state-management";
import { IRole, RoleService } from "@gtmhub/roles";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { hasCurrentUserRole } from "../current-user-storage";
import { USER_ROLES_VIEWONLY } from "@gtmhub/shared/utils/user-roles";
import { SUBSCRIPTION_TYPE_RESTRICTED, SUBSCRIPTION_TYPE_UNCHARGEABLE } from "@gtmhub/shared/utils/subscription-types";
import { IModalService, IModalServiceInstance } from "angular-ui-bootstrap";
import { IdMap } from "@gtmhub/util";

const bindStateToScope = (state: ICustomFieldsStoreState & IAssigneesStoreState): Record<string, unknown> => {
  return {
    customFields: { items: state.customFields.map.user },
    assigneesIdMap: state.assignees.map,
  };
};

interface IUserDetailsViewIndicators {
  loading: boolean;
  deleting: boolean;
  activating: boolean;
  deactivating: boolean;
  assigning: boolean;
  gettingUser: boolean;
}

type IUserDetailsViewIndicatorType = "loading" | "deleting" | "activating" | "deactivating" | "assigning" | "gettingUser" | "admin";

interface IUsersRootScopeService extends IRootScopeService {
  indicators: IUserDetailsViewIndicators;
  noNavigation: boolean;
}

export interface IUserDetailsViewScope extends IScope {
  assignedRoles: IRole[];
  assignedRolesMap: IdMap<boolean>;
  profile: IProfile;
  user: IUser;
  customFields: ICollection<ICustomField>;
  assigneesIdMap: IAssigneeIdMap;
  roles: ICollection<IRole>;
  indicators: IUserDetailsViewIndicators;
  isAdmin: boolean;
  isCurrentUserProfile: boolean;
  accountLicensesCount: number;
  isLastRole(role: IRole): boolean;
  isCurrentUserAdminRole(role: IRole): boolean;
  toggleRole(role: IRole): void;
  checkIfAssignedRole(role: IRole): boolean;
  broadcastUserChanged(): void;
  broadcastUserDeleted(): void;
  returnToState(): void;
  handleUserStatusChange(): void;
  handleUserDeleted(): void;
}

export class UserDetailsViewCtrl implements IStateInit {
  public static $inject = [
    "$rootScope",
    "$scope",
    "$stateParams",
    "$state",
    "$q",
    "$ngRedux",
    "$uibModal",
    "UserService",
    "RoleService",
    "UserProfileService",
    "UIErrorHandlingService",
    "AccountService",
    "CustomFieldActions",
    "AssigneeActions",
  ];

  constructor(
    private $rootScope: IUsersRootScopeService & ITraceRootScopeService,
    private $scope: IUserDetailsViewScope,
    private $stateParams: StateParams,
    private $stateService: StateService,
    private $q: IQService,
    private $ngRedux: INgRedux,
    private $uibModal: IModalService,
    private userService: UserService,
    private roleService: RoleService,
    private profileService: UserProfileService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private accountService: AccountService,
    private customFieldActions: CustomFieldActions,
    private assigneeActions: AssigneeActions
  ) {
    this.$scope.indicators = {
      loading: false,
      deleting: false,
      activating: false,
      deactivating: false,
      assigning: false,
      gettingUser: false,
    };
    this.$scope.profile = this.profileService.getProfile();
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.toggleRole = this.toggleRole;
    this.$scope.checkIfAssignedRole = this.checkIfAssignedRole;
    this.$scope.isLastRole = this.isLastRole;
    this.$scope.isCurrentUserAdminRole = this.isCurrentUserAdminRole;
    this.$scope.broadcastUserChanged = this.broadcastUserChanged;
    this.$scope.broadcastUserDeleted = this.broadcastUserDeleted;
    this.$scope.returnToState = this.returnToState;
    this.$scope.handleUserStatusChange = this.handleUserStatusChange;
    this.$scope.handleUserDeleted = this.handleUserDeleted;

    //currently the controller makes a get request every time there is a change on user, this is prone to bugs and unnecessary. Should be refactored some sunny day.
    this.$scope.$on("usersChanged", this.onUsersChanged);
    this.$scope.$on("userSubscriptionUpdated", this.userChanged);
    this.$scope.$on("$destroy", unsubscribe);
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private userId: string;
  private viewOnlyRole?: IRole;
  private hasAccountRestrictedLicense: boolean;
  private isUserUnchargeableLicense: boolean;

  private init = (): IPromise<unknown> => {
    const rolesPromise: IPromise<ICollection<IRole>> = this.roleService.getRoles();
    this.userId = this.$stateParams.employeeId || this.$stateParams.id;
    const userPromise: IPromise<IUser> = this.userService.getUser(this.userId);
    const accountSubscriptionPromise: IPromise<IAccountSubscription> = this.accountService.getSubscription();

    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
    this.$ngRedux.dispatch(this.customFieldActions.getCustomFields());

    this.$scope.indicators.loading = true;

    this.$scope.$watch("assignedRoles", () => {
      if (this.$scope.assignedRoles) {
        this.$scope.assignedRolesMap = {};

        for (const role of this.$scope.assignedRoles) {
          this.$scope.assignedRolesMap[role.id] = true;
        }
      }
    });

    return this.$q
      .all({
        roles: rolesPromise,
        user: userPromise,
        accountSubscription: accountSubscriptionPromise,
      })
      .then(
        ({ roles, user, accountSubscription }) => {
          this.$scope.indicators.loading = false;

          this.$scope.roles = roles;

          this.viewOnlyRole = roles.items.find((role) => role.name === USER_ROLES_VIEWONLY);
          this.$scope.isAdmin = hasCurrentUserRole("admin");

          this.$scope.accountLicensesCount =
            accountSubscription && accountSubscription.items && angular.isArray(accountSubscription.items) ? accountSubscription.items.length : 0;

          this.hasAccountRestrictedLicense = accountSubscription?.items?.length > 0 && accountSubscription.items.some((s) => s.type === SUBSCRIPTION_TYPE_RESTRICTED);

          this.processUser(user);
          this.$scope.isCurrentUserProfile = user.email === this.$scope.profile.email;
        },
        (err) => this.handleIndicatorError(err, { indicator: "loading", inline: false })
      );
  };

  private onUsersChanged = (): void => {
    this.$scope.indicators.gettingUser = true;

    this.userService.getUser(this.userId).then(
      (user) => {
        this.$scope.indicators.gettingUser = false;

        this.processUser(user);
      },
      (err) => this.handleIndicatorError(err, { indicator: "gettingUser", inline: true })
    );
  };

  private userChanged = (ev, data: { id: string; subscriptionType: string }) => {
    if (this.$scope.user.id === data.id) {
      this.$scope.user.subscriptionType = data.subscriptionType;
    }
  };

  private processUser = (user: IUser): void => {
    user.roles = user.roles || [];

    this.$scope.assignedRoles = user.roles;

    this.$scope.user = user;
    this.$scope.user.customFields = this.$scope.user.customFields || {};

    this.isUserUnchargeableLicense = user.subscriptionType === SUBSCRIPTION_TYPE_UNCHARGEABLE;

    if (this.$scope.isCurrentUserProfile) {
      const profile = this.profileService.getProfile();
      profile.picture = user.picture;
      this.profileService.userProfileUpdated(profile);
    }
  };

  private isCurrentUserAdminRole = (role: IRole): boolean => role.name === "admin" && this.$scope.isCurrentUserProfile;

  private isLastRole = (role: IRole): boolean => {
    const assignedRolesWithoutTeams = this.$scope.assignedRoles.filter((r) => !r.team);
    return assignedRolesWithoutTeams.length === 1 && assignedRolesWithoutTeams[0].id === role.id;
  };

  private checkIfAssignedRole = (role: IRole): boolean => this.$scope.assignedRoles.some((r) => r.id === role.id);

  private removeInvalidRoles = (assignedRole: IRole, role: IRole): IPromise<void> => {
    return this.roleService.removeUserFromRole(assignedRole, this.$scope.user).then(() => {
      this.$scope.assignedRoles = [...this.$scope.assignedRoles.filter((r) => r.id !== assignedRole.id)];

      // Check for role duplication
      if (!this.$scope.assignedRoles.find((r) => r.id === role.id)) {
        this.$scope.assignedRoles = [...this.$scope.assignedRoles, role];
      }

      this.$scope.assignedRolesMap[assignedRole.id] = false;
    });
  };

  /**
   * ensureValidRolesChecked ensures the restricted role is unchecked if a valid role is checked,
   * or all other roles are unchecked if the restricted role is checked
   */
  private ensureValidRolesChecked = (role: IRole): IPromise<void> => {
    const deferred = this.$q.defer<void>();

    if (!this.viewOnlyRole) {
      deferred.resolve();
      return deferred.promise;
    }

    if (this.viewOnlyRole && this.checkIfAssignedRole(this.viewOnlyRole)) {
      return this.removeInvalidRoles(this.viewOnlyRole, role);
    }

    deferred.resolve();
    return deferred.promise;
  };

  private toggleRole = (role: IRole): void => {
    if (this.isLastRole(role) || this.isCurrentUserAdminRole(role)) {
      return;
    }

    this.$rootScope.traceAction("toggle_role", () => {
      this.$scope.indicators.assigning = true;

      if (!this.checkIfAssignedRole(role)) {
        this.addRole(role);

        return;
      }

      this.roleService.removeUserFromRole(role, this.$scope.user).then(
        () => {
          this.$scope.indicators.assigning = false;

          this.$rootScope.$broadcast("usersChanged", true);

          this.$scope.assignedRoles = this.$scope.assignedRoles.filter((r) => r.id !== role.id);
        },
        (err) => this.handleIndicatorError(err, { indicator: "assigning", inline: true })
      );
    });
  };

  private addRole = (role: IRole): void => {
    if (role.id !== this.viewOnlyRole?.id || !this.hasAccountRestrictedLicense || this.isUserUnchargeableLicense) {
      this.addUserToNewRole(role);

      return;
    }

    this.openRestrictedRoleConfirmationModal().result.then(
      // confirm
      () => {
        this.addUserToNewRole(role);
      },
      // cancel
      () => {
        this.$scope.assignedRolesMap[this.viewOnlyRole?.id] = false;
        this.$scope.indicators.assigning = false;
      }
    );
  };

  private addUserToNewRole = (role: IRole) => {
    this.roleService.addUserToRole(role, this.$scope.user).then(
      () => {
        this.$scope.indicators.assigning = false;

        this.ensureValidRolesChecked(role).then(() => {
          this.$rootScope.$broadcast("usersChanged", true);
          this.$rootScope.$broadcast("subscriptionConverted", true);
        });
      },
      (err) => this.handleIndicatorError(err, { indicator: "assigning", inline: true })
    );
  };

  private broadcastUserChanged = (): void => {
    const userChangedArgs: Pick<IUser, "id"> = { id: this.$scope.user.id };
    this.$rootScope.$broadcast("usersChanged", userChangedArgs);
    if (this.$stateService.current.name.includes("employees")) {
      this.$rootScope.$broadcast("employeeChanged");
      this.$rootScope.$broadcast("employeesChanged");
    }
  };

  private broadcastUserDeleted = (): void => {
    if (this.$stateService.current.name.includes("employees")) {
      this.$rootScope.$broadcast("userDeleted", { id: this.$scope.user.id });
      return;
    }
    this.$rootScope.$broadcast("userDeleted");
  };

  private returnToState = (): void => {
    if (this.$stateService.current.name.includes("employees")) {
      this.$stateService.go("gtmhub.employees");
      return;
    }

    this.$stateService.go("gtmhub.usersList");
  };

  private handleUserStatusChange = (): void => {
    this.broadcastUserChanged();
    this.returnToState();
  };

  private handleUserDeleted = (): void => {
    this.broadcastUserDeleted();
    this.returnToState();
  };

  private handleIndicatorError = <E>(err: E, params: { indicator: IUserDetailsViewIndicatorType; inline: boolean }): void => {
    this.uiErrorHandlingService.handleIndicator(err, params);
  };

  private openRestrictedRoleConfirmationModal = (): IModalServiceInstance => {
    return this.$uibModal.open({
      templateUrl: require("../views/restricted-role-confirmation-modal.jade"),
      backdrop: false,
      windowClass: "modal-small",
      backdropClass: "dark-backdrop",
      controller: "RestrictedRoleConfirmationModalCtrl",
    });
  };
}
